import * as React from 'react';
import { Button, Avatar, Box, Paper, IconButton, Menu, MenuItem, SxProps, Theme } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/AuthService';
import {
  clearProfileDisplayCache,
  getCachedProfileDisplay,
  isAvatarUrlLoaded,
  markAvatarUrlLoaded,
  setCachedProfileDisplay,
} from '../services/profileDisplayCache';
import { hostApiClient } from '../hostApiClient';
import { userApiClient } from '../userApiClient';
import { HostProfile, UserProfile } from '../models/Profile';
import MoreVertIcon from '@mui/icons-material/MoreVert';

type BaseProfile = HostProfile | UserProfile;

const getInitialFromName = (name: string): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  const first = parts[0].charAt(0);
  const last = parts[parts.length - 1].charAt(0);
  return (first + last).toUpperCase();
};

const ProfileAvatar: React.FC<{
  profile: BaseProfile | null;
  sx?: SxProps<Theme>;
}> = ({ profile, sx }) => {
  const imageUrl = profile?.profileImageUrl?.trim() || undefined;
  const [imageLoaded, setImageLoaded] = React.useState(() =>
    imageUrl ? isAvatarUrlLoaded(imageUrl) : false
  );

  React.useEffect(() => {
    setImageLoaded(imageUrl ? isAvatarUrlLoaded(imageUrl) : false);
  }, [imageUrl]);

  const showFallback = !imageUrl || !imageLoaded;

  return (
    <Avatar
      src={imageUrl}
      slotProps={{
        img: {
          onLoad: () => {
            if (!imageUrl) return;
            markAvatarUrlLoaded(imageUrl);
            setImageLoaded(true);
          },
        },
      }}
      sx={{
        bgcolor: '#4AA7C6',
        color: '#ffffff',
        ...sx,
      }}
    >
      {showFallback ? (profile ? getInitialFromName(profile.name) : '?') : null}
    </Avatar>
  );
};

const AuthBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [isLoggedIn, setIsLoggedIn] = React.useState(authService.getIsLoggedIn());
  const [profile, setProfile] = React.useState<BaseProfile | null>(() => {
    const cached = getCachedProfileDisplay();
    return cached ? ({ ...cached, roles: authService.getRoles() } as BaseProfile) : null;
  });
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const getProfile = async (): Promise<BaseProfile> => {
    const roles = authService.getRoles();
    const isHost = roles.includes('HOST');
    
    if (isHost) {
      return await hostApiClient.getProfile();
    } else {
      return await userApiClient.getProfile();
    }
  };

  const refreshProfile = React.useCallback(async () => {
    try {
      const newProfile = await getProfile();
      setProfile(newProfile);
      setCachedProfileDisplay({
        name: newProfile.name,
        profileImageUrl: newProfile.profileImageUrl,
      });
    } catch (error) {
      console.error('Error getting profile:', error);
      setProfile(null);
      setCachedProfileDisplay(null);
    }
  }, []);

  React.useEffect(() => {
    if (!isLoggedIn) {
      setProfile(null);
      clearProfileDisplayCache();
      return;
    }

    refreshProfile();

    const handleProfileUpdated = () => {
      const cached = getCachedProfileDisplay();
      if (cached) {
        setProfile((prev) =>
          ({
            ...(prev ?? {}),
            ...cached,
            roles: authService.getRoles(),
          }) as BaseProfile
        );
      }
      refreshProfile();
    };
    window.addEventListener('profile-updated', handleProfileUpdated);
    return () => {
      window.removeEventListener('profile-updated', handleProfileUpdated);
    };
  }, [isLoggedIn, refreshProfile]);

  const handleLogin = () => {
    navigate('/login');
  };

  const handleRegister = () => {
    navigate('/login');
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    authService.logout();
    clearProfileDisplayCache();
    setIsLoggedIn(false);
    setProfile(null);
    navigate('/');
  };

  const handleProfileClick = () => {
    const roles = authService.getRoles();
    const onTutorSection = location.pathname.startsWith('/tutor');
    if (onTutorSection && roles.includes('TUTOR')) {
      navigate('/tutor/listing');
      return;
    }
    if (roles.includes('USER')) {
      navigate('/user/profile');
      return;
    }
    if (roles.includes('TUTOR')) {
      navigate('/tutor/listing');
      return;
    }
    if (roles.includes('HOST')) {
      navigate('/host/organization');
      return;
    }
    navigate('/');
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 0.5 } }}>
      {!isLoggedIn ? (
        <>
          <Button 
            variant="outlined" 
            onClick={handleLogin}
            size="small"
            sx={{ 
              display: { xs: 'none', sm: 'inline-flex' },
              minWidth: { xs: 'auto', sm: '80px' },
              cursor: 'pointer',
              WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)',
              touchAction: 'manipulation'
            }}
          >
            {t('auth.signIn', 'Sign In')}
          </Button>

          {/* Mobile sign in button */}
          <Button 
            variant="outlined" 
            onClick={handleLogin}
            size="small"
            sx={{ 
              display: { xs: 'inline-flex', sm: 'none' },
              minWidth: 'auto',
              px: 1,
              cursor: 'pointer',
              WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)',
              touchAction: 'manipulation'
            }}
          >
            {t('auth.signIn', 'Sign In')}
          </Button>
        </>
      ) : (
        <>
          <Box
            component={Paper}
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: { xs: 0, sm: 0.5 },
              py: { xs: 0, sm: 0.5 },
              borderRadius: 2,
              bgcolor: { xs: 'transparent', sm: 'background.paper' },
            }}
          >
            <IconButton
              onClick={handleProfileClick}
              size="small"
              aria-label={t('navigation.myProfile', 'My Profile')}
              sx={{ p: { xs: 0.25, sm: 0.5 } }}
            >
              <ProfileAvatar
                profile={profile}
                sx={{
                  width: { xs: 32, sm: 40 },
                  height: { xs: 32, sm: 40 },
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                }}
              />
            </IconButton>
          </Box>
          <IconButton 
            onClick={handleMenuOpen}
            size="small"
            sx={{ 
              p: { xs: 0.5, sm: 1 },
              cursor: 'pointer',
              WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)',
              touchAction: 'manipulation'
            }}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            slotProps={{
              paper: {
                sx: {
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)',
                }
              }
            }}
          >
            <MenuItem 
              onClick={() => {
                handleMenuClose();
                handleLogout();
              }}
              sx={{
                cursor: 'pointer',
                WebkitTapHighlightColor: 'rgba(0, 0, 0, 0.1)',
                touchAction: 'manipulation'
              }}
            >
              {t('navigation.logout', 'Logout')}
            </MenuItem>
          </Menu>
        </>
      )}
    </Box>
  );
};

export default AuthBar; 